import * as THREE from 'three';
import { DSTEEL, smoke, std } from '../materials.ts';
import { G, M, V, eyesGlow, limb } from '../prims.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * Arrastrador (docs/MISIONES.md, plunder): a small hunched thing of shadow that never fights. Half a goblin's
 * height, a sack of a body slung low between long thin arms that end in iron hooks, a short chain trailing from
 * one wrist, a hooded head with two pale eyes. Its 12 Hz loop is a low scurry: the body rocks, the arms reach
 * and drag in turn, the chain swings.
 */

export const dragger = (o: BaseOpts = {}): Model => {
  const c = begin(o, 0.45, 71);
  const { g } = c;
  const body = smoke('#100d17', 0.9);
  const deep = std('#09080c', { roughness: 0.6 });
  const hook = DSTEEL;
  const chainM = std('#3b3c42', { roughness: 0.45, metalness: 0.7 });

  const torso = G(g);
  // The sack of a body, slung low and forward.
  const sack = M(new THREE.SphereGeometry(0.2, 9, 7), body, torso);
  sack.position.set(0, 0.34, -0.04);
  sack.scale.set(1.05, 0.85, 1.3);
  sack.rotation.x = -0.35;
  // Hooded head, pushed out ahead of the shoulders.
  const head = G(torso, 0, 0.44, 0.2);
  const hood = M(new THREE.ConeGeometry(0.13, 0.24, 7), deep, head);
  hood.rotation.x = Math.PI / 2 + 0.25;
  hood.position.set(0, 0.02, 0.02);
  eyesGlow(head, '#e9f0ff', 0.0, 0.1, 0.04, 0.016);
  // Ragged hem of shadow under the sack.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const r = M(new THREE.ConeGeometry(0.05, 0.16, 4), body, torso);
    r.position.set(Math.sin(a) * 0.15, 0.17, -0.04 + Math.cos(a) * 0.18);
    r.rotation.x = Math.PI;
  }
  // Long arms reaching to the ground ahead, each with a hook.
  const arms: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const arm = G(torso, k * 0.14, 0.4, 0.1);
    const sh = V(0, 0, 0), el = V(k * 0.08, -0.12, 0.2), wr = V(k * 0.05, -0.36, 0.34);
    limb(arm, sh, el, 0.03, 0.024, deep, 5);
    limb(arm, el, wr, 0.024, 0.018, deep, 5);
    const h = M(new THREE.TorusGeometry(0.05, 0.012, 4, 8, Math.PI * 1.3), hook, arm);
    h.position.set(wr.x, wr.y + 0.02, wr.z + 0.04);
    h.rotation.set(0, Math.PI / 2, Math.PI * 0.6);
    arms.push(arm);
  }
  // A short chain off the left wrist, trailing behind.
  const chain = G(arms[0] as THREE.Group, -0.05, -0.34, 0.3);
  for (let i = 0; i < 4; i++) {
    const l = M(new THREE.TorusGeometry(0.025, 0.007, 3, 6), chainM, chain);
    l.position.set(0, 0, -0.045 * i);
    l.rotation.y = i % 2 ? Math.PI / 2 : 0;
  }
  // Two short legs, bent.
  for (const k of [-1, 1]) limb(torso, V(k * 0.09, 0.24, -0.14), V(k * 0.11, 0.0, -0.08), 0.03, 0.022, deep, 5);

  const tick = (n: number): void => {
    const t = (n % 4) / 4;
    const s = Math.sin(t * Math.PI * 2);
    torso.rotation.z = 0.08 * s;
    torso.position.y = 0.02 * Math.abs(s);
    (arms[0] as THREE.Group).rotation.x = 0.35 * s;
    (arms[1] as THREE.Group).rotation.x = -0.35 * s;
    chain.rotation.y = 0.4 * s;
  };
  tick(0);
  return done(c, 0.62, tick);
};
